<template>
  <a-input
    v-model:value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="maxDisplayLength"
    :status="computedStatus"
    class="ant-iban-input"
    @update:value="onInput"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template #prefix>
      <i class="fas fa-university" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  /** Emit raw (không khoảng trắng), mặc định true */
  unmasked?: boolean;
  validateOnBlur?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "VD: VN12 ACCT-000009",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  unmasked: true,
  validateOnBlur: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "validate", valid: boolean): void;
}>();

const displayValue = ref("");
const maxDisplayLength = 42; // 34 chars + spaces

const onlyIbanChars = (value: string) =>
  value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 34);

const formatIban = (raw: string) => raw.replace(/(.{4})/g, "$1 ").trim();

/** IBAN checksum (mod 97) */
const isValidIban = (iban: string) => {
  if (!iban) return true;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const expanded = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));
  let remainder = 0;
  for (const digit of expanded) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
};

watch(
  () => props.modelValue,
  (val) => {
    displayValue.value = formatIban(onlyIbanChars(val ?? ""));
  },
  { immediate: true }
);

const localError = computed(() => {
  if (!props.validateOnBlur) return false;
  const raw = onlyIbanChars(props.modelValue ?? "");
  return !!raw && !isValidIban(raw);
});

const computedStatus = computed(() => props.status || (localError.value ? "error" : ""));

const onInput = (val: string) => {
  const raw = onlyIbanChars(val);
  displayValue.value = formatIban(raw);
  const emitValue = props.unmasked ? raw : displayValue.value;
  emit("update:modelValue", emitValue);
  emit("change", emitValue);
};

const onBlur = (e: FocusEvent) => {
  emit("validate", isValidIban(onlyIbanChars(props.modelValue ?? "")));
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — IBAN
  Global: <h-iban v-model="iban" @validate="ok => {}" />
  Import: import { IbanInput } from '@/components/ant/input'

  Props: modelValue, unmasked, validateOnBlur, status...
  Events: update:modelValue, change, blur, pressEnter, validate
  Docs: src/components/ant/input/README.md
-->
